import { apiClient } from './client';

/**
 * Student 360 for staff: GET /crm/students, /crm/students/summary and /crm/students/:id.
 *
 * The server decides what a staff member may see and says so in `meta.groups`, `meta.presence` and
 * `meta.learning`; a key of a group the viewer does not hold is simply absent. Callers read those and
 * test for keys, they never look at the role.
 *
 * The backend DTOs are `forbidNonWhitelisted`: a query param that is not declared, or whose value is
 * outside the lists below, answers 400. So every function here sends only the params of its route,
 * omits empty ones and drops values the contract does not know (the value lists mirror
 * darsly-backend-new/src/modules/student-360/contract.ts). There is no cache-busting `_` param on
 * purpose: apiClient never serves a GET from its cache, and an undeclared param would be a 400.
 */

export const STUDENT_STATUS_FILTERS = ['active', 'completed', 'inactive', 'none'];
export const STUDENT_ACTIVITY_FILTERS = [
  'online', 'today', '7d', 'inactive3', 'inactive7', 'inactive14', 'inactive30', 'never',
];
export const STUDENT_SORT_FIELDS = ['lastSeen', 'progress', 'name', 'joined', 'enrolled'];
/** These two sort a selected course's enrollment: the API refuses them without a courseId. */
export const COURSE_SCOPED_SORTS = ['progress', 'enrolled'];
export const STUDENT_DETAIL_INCLUDES = ['learning', 'results', 'practice', 'orders', 'tickets'];

/** What the API sorts by when `sort` and `order` are left out. */
export const DEFAULT_SORT = 'lastSeen';
export const DEFAULT_ORDER = 'desc';
export const STUDENT_LIST_DEFAULT_LIMIT = 25;
export const STUDENT_LIST_MAX_LIMIT = 50;
/** The API answers 400 to a page number above this (the DTO's bound, as in the admin panel). */
export const STUDENT_LIST_MAX_PAGE = 100000;
export const STUDENT_SEARCH_MAX_LENGTH = 100;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Ids in the path and in `courseId` are UUIDs; anything else is a 400, so it is never sent. */
export function isUuid(value) {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

function wholeNumber(value) {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

/**
 * The query string of GET /crm/students for a UI query: only declared params, only valid values.
 * `search` is trimmed and cut to 100 characters; `page` is sent from the second page on; `limit`,
 * `sort` and `order` only when they differ from the server's defaults.
 * @param {{ search?, courseId?, status?, activity?, atRisk?, sort?, order?, page?, limit? }} query
 */
export function buildStudentListParams(query = {}) {
  const params = {};

  const search = typeof query.search === 'string'
    ? query.search.trim().slice(0, STUDENT_SEARCH_MAX_LENGTH)
    : '';
  if (search) params.search = search;

  const courseId = isUuid(query.courseId) ? query.courseId : '';
  if (courseId) params.courseId = courseId;

  if (STUDENT_STATUS_FILTERS.includes(query.status)) params.status = query.status;
  if (STUDENT_ACTIVITY_FILTERS.includes(query.activity)) params.activity = query.activity;

  // Only "just the at-risk students" is asked for. `atRisk=false` (just the others) exists in the
  // API but no screen needs it, and a UI that keeps "off" as false must not send it by accident.
  if (query.atRisk === true || query.atRisk === 'true') params.atRisk = true;

  // The server's defaults (last seen, descending) are left out: asking for them changes nothing.
  if (STUDENT_SORT_FIELDS.includes(query.sort)
    && query.sort !== DEFAULT_SORT
    && (courseId || !COURSE_SCOPED_SORTS.includes(query.sort))) {
    params.sort = query.sort;
  }
  if (query.order === 'asc') params.order = 'asc';

  const page = wholeNumber(query.page);
  if (page !== null && page > 1) params.page = Math.min(page, STUDENT_LIST_MAX_PAGE);
  const limit = wholeNumber(query.limit);
  if (limit !== null && limit >= 1 && limit <= STUDENT_LIST_MAX_LIMIT && limit !== STUDENT_LIST_DEFAULT_LIMIT) {
    params.limit = limit;
  }

  return params;
}

/**
 * The query string of GET /crm/students/:id. `include` takes an array or a comma separated string of
 * facets; unknown facets are dropped (the API answers 400 to them), the rest are sent comma
 * separated without spaces. A facet the viewer's groups do not allow is left out by the server.
 */
export function buildStudentDetailParams({ include, courseId } = {}) {
  const params = {};
  const wanted = (Array.isArray(include) ? include : String(include || '').split(','))
    .map((facet) => String(facet).trim())
    .filter((facet) => STUDENT_DETAIL_INCLUDES.includes(facet));
  const facets = STUDENT_DETAIL_INCLUDES.filter((facet) => wanted.includes(facet));
  if (facets.length) params.include = facets.join(',');
  if (isUuid(courseId)) params.courseId = courseId;
  return params;
}

function pagedBody(body) {
  if (Array.isArray(body)) return { data: body, meta: null };
  return { data: Array.isArray(body?.data) ? body.data : [], meta: body?.meta || null };
}

/**
 * One page of students the signed-in staff member may open.
 * Resolves { data: rows, meta } where meta is the paging numbers plus groups, presence, learning,
 * leadPool and generatedAt. Keys of fields the viewer lacks are absent from the rows.
 * @param {object} query see buildStudentListParams
 */
export async function getStudents(query = {}) {
  const { data } = await apiClient.get('/crm/students', { params: buildStudentListParams(query) });
  return pagedBody(data);
}

/**
 * KPI counts over the students the viewer may open (optionally one course): total, activeToday,
 * active7d, inactive7d, neverSeen, atRisk, and onlineNow only for a viewer with exact presence.
 * @param {{ courseId?: string }} [query]
 */
export async function getStudentSummary({ courseId } = {}) {
  const params = isUuid(courseId) ? { courseId } : {};
  const { data } = await apiClient.get('/crm/students/summary', { params });
  return data;
}

/**
 * One student: identity, presence and enrollments always (when the viewer holds their groups), plus
 * the requested facets. A student that does not exist and one outside the viewer's reach both
 * reject with code 404.
 * @param {string} studentId
 * @param {{ include?: string[]|string, courseId?: string }} [options]
 */
export async function getStudent360(studentId, options = {}) {
  const { data } = await apiClient.get(
    `/crm/students/${encodeURIComponent(studentId)}`,
    { params: buildStudentDetailParams(options) },
  );
  return data;
}
